// Fuente canónica; scripts/syncDataContracts.js mantiene las copias de clientes.
const {
  parsePhoneNumberFromString,
  formatIncompletePhoneNumber,
} = require('libphonenumber-js/max');

class DataValidationError extends Error {
  constructor(message) {
    super(message);
    this.code = 'invalid-argument';
  }
}
const invalid = (message) => {
  throw new DataValidationError(message);
};
const text = (
  value,
  { field = 'El campo', required = true, min = 1, max = 120 } = {}
) => {
  if ((value === undefined || value === null || value === '') && !required)
    return '';
  if (typeof value !== 'string') invalid(`${field} debe ser texto`);
  const result = value.trim();
  if (
    (!result && required) ||
    (result && (result.length < min || result.length > max))
  )
    invalid(`${field} debe tener entre ${min} y ${max} caracteres`);
  return result;
};
const name = (value, { required = true, field = 'El nombre' } = {}) =>
  text(value, { field, min: 2, required });
const email = (value, { required = false } = {}) => {
  const result = text(value, {
    field: 'El email',
    required,
    max: 254,
  }).toLowerCase();
  if (result && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(result))
    invalid('Ingresá un email válido');
  return result;
};
const dni = (value) => {
  const raw = text(value, { field: 'El DNI', max: 24 });
  if (!/^[\d.\s-]+$/.test(raw))
    invalid('El DNI sólo puede contener dígitos y separadores');
  const result = raw.replace(/\D/g, '');
  if (!/^\d{6,9}$/.test(result))
    invalid('El DNI debe tener entre 6 y 9 dígitos');
  return result;
};
const cuit = (value, { isJuridica = false } = {}) => {
  const raw = text(value, { field: 'El CUIT', max: 20 });
  if (!/^[\d\s-]+$/.test(raw)) invalid('El CUIT no es válido');
  const result = raw.replace(/\D/g, '');
  if (!/^\d{11}$/.test(result)) invalid('El CUIT debe tener 11 dígitos');
  if (isJuridica && !['30', '33', '34'].includes(result.slice(0, 2)))
    invalid('El CUIT jurídico debe comenzar con 30, 33 o 34');
  const sum = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2].reduce(
    (acc, weight, i) => acc + weight * Number(result[i]),
    0
  );
  const remainder = sum % 11;
  if (
    Number(result[10]) !==
    (remainder === 0 ? 0 : remainder === 1 ? 9 : 11 - remainder)
  )
    invalid('El CUIT no es válido (dígito verificador incorrecto)');
  return result;
};

const parsedPhone = (value) => {
  if (typeof value !== 'string' || !/^[+\d\s().-]+$/.test(value.trim()))
    return undefined;
  let input = value.trim();
  if (input.startsWith('00')) input = `+${input.slice(2)}`;
  const digits = input.replace(/\D/g, '');
  if (!input.startsWith('+') && /^54(?:\d{10}|9\d{10})$/.test(digits))
    input = `+${digits}`;
  return parsePhoneNumberFromString(input, {
    defaultCountry: 'AR',
    extract: false,
  });
};
const phone = (value, { required = false, previous } = {}) => {
  const raw = text(value, { field: 'El teléfono', required, max: 40 });
  if (!raw) return '';
  const parsed = parsedPhone(raw);
  if (!parsed || !parsed.isValid() || parsed.ext) {
    // Compatibilidad acotada: sólo conserva un teléfono histórico incompleto
    // al editarlo sin cambios. Nunca habilita su alta ni inventa dígitos.
    if (value === previous && /^\d{9}$/.test(raw)) return raw;
    invalid(
      'Ingresá un teléfono válido con código de área (o prefijo internacional)'
    );
  }
  return parsed.number;
};
const splitPhone = (value) => {
  const raw = typeof value === 'string' ? value : '';
  const parsed = parsedPhone(raw);
  if (parsed?.country === 'AR') {
    const national =
      parsed.nationalNumber.length === 11 &&
      parsed.nationalNumber.startsWith('9')
        ? parsed.nationalNumber.slice(1)
        : parsed.nationalNumber;
    const formatted = formatIncompletePhoneNumber(national, 'AR');
    const parts = formatted.split(/[\s-]+/);
    if (parts.length > 1) {
      const area = parts.shift().replace(/^0/, '');
      const number = parts.join('').replace(/\D/g, '');
      return { area, number };
    }
  }
  // Internacional sin país AR: conservarlo completo, no reinterpretar el área.
  return { area: '', number: raw };
};
const joinPhone = (area, number, { previous } = {}) => {
  if (!area) return phone(number, { previous });
  if (area.startsWith('0')) invalid('El código de área no debe comenzar con 0');
  if (number.startsWith('15')) invalid('El número no debe comenzar con 15');
  if (!/^\d{2,4}$/.test(area) || area.startsWith('0') || !/^\d+$/.test(number))
    invalid('Ingresá código de área (2–4 dígitos, sin 0) y número');
  const raw = `${area}${number}`;
  if (
    previous &&
    raw === String(previous).replace(/\D/g, '') &&
    raw.length === 9
  )
    return phone(raw, { previous });
  const old = parsedPhone(previous);
  const input =
    old?.country === 'AR' && old.getType() === 'MOBILE' ? `+549${raw}` : raw;
  return phone(input, { required: true });
};
const whatsAppPhone = (value) => {
  try {
    return phone(value).replace(/^\+/, '');
  } catch {
    return '';
  }
};
const number = (
  value,
  {
    field = 'El valor',
    required = true,
    defaultValue = 0,
    min = 0,
    max = Number.MAX_SAFE_INTEGER,
    integer = false,
  } = {}
) => {
  if (value === undefined || value === null || value === '') {
    if (!required) return defaultValue;
    invalid(`${field} es requerido`);
  }
  if (
    !['string', 'number'].includes(typeof value) ||
    (typeof value === 'string' && !/^\d+(?:\.\d+)?$/.test(value.trim()))
  )
    invalid(`${field} debe ser numérico`);
  const result = Number(value);
  if (
    !Number.isFinite(result) ||
    result < min ||
    result > max ||
    (integer && !Number.isInteger(result))
  )
    invalid(`${field} está fuera del rango válido`);
  return result;
};
const year = (value) =>
  number(value, {
    field: 'El año',
    integer: true,
    min: 1886,
    max: new Date().getUTCFullYear() + 2,
  });
const kms = (value, options = {}) =>
  number(value, { field: 'Los kilómetros', integer: true, ...options });
const plate = (value, { required = true } = {}) => {
  const result = text(value, { field: 'La patente', required, max: 20 })
    .toUpperCase()
    .replace(/[\s-]/g, '');
  if (result && !/^[A-Z0-9]{5,10}$/.test(result))
    invalid('La patente no tiene un formato válido');
  return result;
};
const nameSearch = (value) =>
  text(value, { field: 'El nombre', max: 120 })
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ');
const pickupLocations = (value) => {
  if (!Array.isArray(value) || value.length > 100)
    invalid('Las ubicaciones de retiro deben ser una lista de direcciones');
  return [
    ...new Set(
      value.map((item) =>
        text(item, { field: 'La ubicación de retiro', max: 300 })
      )
    ),
  ];
};
const DEFAULT_APPRAISAL_CFG = Object.freeze(
  [0, 1, 3].map((id) => Object.freeze({ id, days: 7 }))
);
const appraisalCfg = (value) => {
  if (!Array.isArray(value) || value.length > 3)
    invalid('La configuración de peritajes debe ser una lista');
  const ids = new Set();
  return value
    .map((entry) => {
      if (
        !entry ||
        typeof entry !== 'object' ||
        ![0, 1, 3].includes(entry.id) ||
        ids.has(entry.id)
      )
        invalid('Estado de configuración inválido o duplicado');
      ids.add(entry.id);
      return {
        id: entry.id,
        days: number(entry.days, {
          field: 'Los días',
          integer: true,
          min: 1,
          max: 3650,
        }),
      };
    })
    .sort((a, b) => a.id - b.id);
};
const effectiveAppraisalCfg = (unit, dealership) => {
  for (const candidate of [unit, dealership, DEFAULT_APPRAISAL_CFG]) {
    try {
      return appraisalCfg(candidate);
    } catch {
      /* dato histórico: probar siguiente fuente */
    }
  }
  return [];
};
const isSuccess = (result) =>
  typeof result?.ok === 'boolean'
    ? result.ok
    : result?.success === true || result?.success === 'yes';
const crudResult = (result) => ({ ...result, ok: isSuccess(result) });

module.exports = {
  DataValidationError,
  text,
  name,
  email,
  dni,
  cuit,
  phone,
  splitPhone,
  joinPhone,
  whatsAppPhone,
  number,
  year,
  kms,
  plate,
  nameSearch,
  pickupLocations,
  DEFAULT_APPRAISAL_CFG,
  appraisalCfg,
  effectiveAppraisalCfg,
  isSuccess,
  crudResult,
};
